import React from 'react';

import { useChannel } from '../../channels/provider';
import { detectCountryFromTimezone } from '../../countries';
import { createSafeContext } from '../../safe-context';
import { CheckoutCartFragment, CountryCode } from '../../saleor-graphql';
import {
  CartProvider as ClientCartProvider,
  CartProviderProps as ClientCartProviderProps,
  useCart as useClientCart,
} from './use-cart';

interface ICartContext {
  cart: CheckoutCartFragment | null;
  country: CountryCode;
  setCountry: (country: CountryCode) => void;
  detectedCountry: CountryCode | null;
  isCartLoaded: boolean;
  isEmpty: boolean;
}

const [useCart, Provider] = createSafeContext<ICartContext>();

function CartProviderInner({ children }: { children: React.ReactNode }) {
  const clientCart = useClientCart();
  const { channel } = useChannel();
  const [detectedCountry, setDetectedCountry] = React.useState<CountryCode | null>(null);
  const [country, setCountry] = React.useState<CountryCode>(channel.defaultCountry as CountryCode);

  const cart = clientCart.cart as CheckoutCartFragment | null;
  const isCartLoaded = !clientCart.isLoading;
  const isEmpty = clientCart.isEmpty;

  // Update country from cart if available
  React.useEffect(() => {
    if (cart?.shippingAddress?.country?.code) {
      const checkoutCountry = cart.shippingAddress.country.code as CountryCode;
      setCountry(checkoutCountry);
    }
  }, [cart]);

  // Detect country from timezone once cart is loaded
  React.useEffect(() => {
    if (!isCartLoaded) return;
    if (typeof window === 'undefined') return;
    if (detectedCountry) return;

    const detected = detectCountryFromTimezone();
    if (detected) {
      setDetectedCountry(detected);
    }
  }, [isCartLoaded, detectedCountry]);

  return (
    <Provider value={{ cart, country, setCountry, detectedCountry, isCartLoaded, isEmpty }}>
      {children}
    </Provider>
  );
}

interface CartProviderProps extends ClientCartProviderProps {}

const CartProvider = (props: CartProviderProps) => {
  return (
    <ClientCartProvider {...props}>
      <CartProviderInner>{props.children}</CartProviderInner>
    </ClientCartProvider>
  );
};

export { useCart, CartProvider, type CartProviderProps };
